/**
 * حالت روشن/تاریک.
 *
 * سه قاعده:
 *   ۱. پیش‌فرض از سیستم کاربر می‌آید، نه از سلیقه‌ی ما.
 *   ۲. انتخاب دستی در `localStorage` می‌ماند و از سیستم پیشی می‌گیرد.
 *   ۳. اعمالش یک کلاس روی ریشه است (`.dark`)، پس هیچ جزئی لازم نیست حالت را
 *      از context بگیرد؛ CSS خودش پالت را عوض می‌کند.
 */

import { useEffect, useState } from 'react'

export type ThemeMode = 'light' | 'dark'

const STORAGE_KEY = 'di-theme'

/** حالت ذخیره‌شده، و در نبودش خواسته‌ی سیستم. */
export const preferredMode = (): ThemeMode => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark') {
      return saved
    }
  } catch {
    // حالت خصوصی مرورگر می‌تواند دسترسی به storage را ببندد؛ سلیقه‌ی سیستم کافی است.
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** کلاس ریشه را با حالت هم‌راست می‌کند. */
export const applyMode = (mode: ThemeMode): void => {
  document.documentElement.classList.toggle('dark', mode === 'dark')
}

export const useThemeMode = (): { mode: ThemeMode; toggle: () => void } => {
  const [mode, setMode] = useState<ThemeMode>(preferredMode)

  // این effect فقط DOM را لمس می‌کند و state تازه‌ای نمی‌سازد.
  useEffect(() => {
    applyMode(mode)
  }, [mode])

  const toggle = (): void => {
    setMode((current) => {
      const next: ThemeMode = current === 'dark' ? 'light' : 'dark'
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // ذخیره نشدن انتخاب، دلیل خوبی برای شکستن تعویض حالت نیست.
      }
      return next
    })
  }

  return { mode, toggle }
}
